import React, { useMemo } from 'react';
import { formatCurrency } from '../../../Help/helper';
import { computeBudgetReview } from '../utils/reportAggregations';
import { BUDGET_SEGMENTS } from '../constants';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const BudgetReviewTab = ({ data }) => {
  const { monthInvoices, monthStart, jobsByNo, customersByName } = data;

  const { weeks, segmentData } = useMemo(
    () => computeBudgetReview(monthInvoices, monthStart, jobsByNo, customersByName),
    [monthInvoices, monthStart, jobsByNo, customersByName]
  );

  const monthLabel = `${MONTH_NAMES[monthStart.getMonth()]} ${monthStart.getFullYear()}`;

  // Compute grand totals per week
  const weekTotals = weeks.map((_, wi) =>
    BUDGET_SEGMENTS.reduce(
      (sum, seg) => sum + (segmentData[seg]?.spareParts[wi] || 0) + (segmentData[seg]?.labour[wi] || 0),
      0
    )
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3">
          <h3 className="text-lg font-bold text-white">Budget Review — {monthLabel}</h3>
          <p className="text-indigo-200 text-sm">Revenue projections vs actuals by service segment</p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Segment</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                {weeks.map((w, i) => (
                  <th key={i} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{w.label}</th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {BUDGET_SEGMENTS.map(seg => {
                const spData = segmentData[seg]?.spareParts || [];
                const lbData = segmentData[seg]?.labour || [];
                const spTotal = spData.reduce((s, v) => s + v, 0);
                const lbTotal = lbData.reduce((s, v) => s + v, 0);
                const segTotal = spTotal + lbTotal;

                return (
                  <React.Fragment key={seg}>
                    {/* Spare Parts row */}
                    <tr className="hover:bg-gray-50">
                      <td rowSpan={2} className="px-4 py-3 text-sm font-semibold text-gray-900 align-middle border-r border-gray-100">
                        {seg}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600">Parts</td>
                      {spData.map((v, i) => (
                        <td key={i} className="px-4 py-2 text-sm text-right text-gray-700">
                          {v > 0 ? formatCurrency(v) : '-'}
                        </td>
                      ))}
                      <td className="px-4 py-2 text-sm text-right font-medium text-gray-800">
                        {spTotal > 0 ? formatCurrency(spTotal) : '-'}
                      </td>
                    </tr>
                    {/* Labour row */}
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs text-gray-600">Labour</td>
                      {lbData.map((v, i) => (
                        <td key={i} className="px-4 py-2 text-sm text-right text-gray-700">
                          {v > 0 ? formatCurrency(v) : '-'}
                        </td>
                      ))}
                      <td className="px-4 py-2 text-sm text-right font-medium text-gray-800">
                        {lbTotal > 0 ? formatCurrency(lbTotal) : '-'}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
              {/* Grand totals */}
              <tr className="bg-gray-100 font-semibold">
                <td colSpan={2} className="px-4 py-3 text-sm text-gray-900">Week Totals</td>
                {weekTotals.map((v, i) => (
                  <td key={i} className="px-4 py-3 text-sm text-right text-indigo-700">
                    {v > 0 ? formatCurrency(v) : '-'}
                  </td>
                ))}
                <td className="px-4 py-3 text-sm text-right text-indigo-800">
                  {formatCurrency(weekTotals.reduce((s, v) => s + v, 0))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BudgetReviewTab;
